/**CORE LIBRARIES IMPORT */
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";

/**DATA / HOOKS / UTILS IMPORT */
import { getEventBySlug, formatEventDate } from "../data/events";
import useSiteSettings from "../hooks/useSiteSettings";
import { buildWhatsAppLink } from "../utils/whatsapp";

/** Official WhatsApp glyph (brand mark, unmodified). */
const WhatsAppGlyph = () => (
  <svg viewBox="0 0 24 24" width="30" height="30" aria-hidden="true" focusable="false">
    <path
      fill="currentColor"
      d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"
    />
  </svg>
);

export const BUBBLE_DELAY_MS = 6000;
// Session storage only (cleared with the tab): a strictly necessary UI preference,
// so no cookie consent is needed. See the Privacy Policy, section 7.
const BUBBLE_KEY = "gw-whatsapp-bubble";

/** Pages with forms or payment: no greeting bubble there (the button stays). */
export const isFormPage = (pathname) => /^\/(book\b|booking\/|events\/[^/]+\/book)/.test(pathname);

// Remove the persistent value earlier versions stored in localStorage.
try {
  localStorage.removeItem(BUBBLE_KEY);
} catch {
  /* storage unavailable */
}

/** The greeting is shown at most once per browser session. */
export function bubbleAllowed() {
  try {
    return !sessionStorage.getItem(BUBBLE_KEY);
  } catch {
    return false; // storage blocked: do not risk showing it on every page
  }
}
function rememberBubble() {
  try {
    sessionStorage.setItem(BUBBLE_KEY, "1");
  } catch {
    /* ignore */
  }
}

/**
 * Floating WhatsApp contact button (bottom-right, above the safe area).
 * Hidden until a valid international number is set in the portal and the
 * widget is enabled there. A small greeting bubble appears once after a
 * short delay and can be dismissed; it never appears on form/payment pages.
 */
const WhatsAppButton = () => {
  const settings = useSiteSettings();
  const { pathname } = useLocation();
  const [bubble, setBubble] = useState(false);
  const wa = settings.whatsapp || {};

  const slug = pathname.match(/^\/events\/([^/]+)/)?.[1];
  const event = slug ? getEventBySlug(slug) : null;
  const message = event
    ? `Hello The Grey Wolf, I have a question about the ${event.title} on ${formatEventDate(event.date)}.`
    : wa.defaultMessage;
  const href = wa.popupEnabled === false ? null : buildWhatsAppLink(wa.number, message);
  const onFormPage = isFormPage(pathname);

  // Reserve space at the bottom of the page so the button never hides content.
  useEffect(() => {
    if (!href) return undefined;
    document.body.classList.add("has-whatsapp");
    return () => document.body.classList.remove("has-whatsapp");
  }, [href]);

  useEffect(() => {
    if (!href || onFormPage || !bubbleAllowed()) {
      setBubble(false);
      return undefined;
    }
    const t = setTimeout(() => {
      setBubble(true);
      rememberBubble();
    }, BUBBLE_DELAY_MS);
    return () => clearTimeout(t);
  }, [href, onFormPage]);

  if (!href) return null;

  const dismiss = () => {
    setBubble(false);
    rememberBubble();
  };

  return (
    <div className="whatsapp-widget">
      <AnimatePresence>
        {bubble && (
          <motion.div
            className="whatsapp-bubble"
            role="status"
            initial={{ opacity: 0, y: 12, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          >
            <a href={href} target="_blank" rel="noopener noreferrer" className="whatsapp-bubble-text" onClick={dismiss}>
              {wa.popupText}
            </a>
            <button type="button" className="whatsapp-bubble-close" onClick={dismiss} aria-label="Dismiss WhatsApp message">
              <span aria-hidden="true">×</span>
            </button>
          </motion.div>
        )}
      </AnimatePresence>
      <motion.a
        className="whatsapp-button"
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Chat with us on WhatsApp (opens in a new tab)"
        onClick={() => bubble && dismiss()}
        initial={{ opacity: 0, scale: 0.6 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6, delay: 1, ease: [0.22, 1, 0.36, 1] }}
        whileHover={{ y: -4, scale: 1.06 }}
        whileTap={{ scale: 0.94 }}
      >
        <WhatsAppGlyph />
      </motion.a>
    </div>
  );
};

export default WhatsAppButton;
